import { Link } from "react-router";
import { styled } from "styled-components";

import { useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { interpolate } from "domains/language/interpolate";
import { getTMLicense } from "entities/permissions/selectors";
import type { Topic } from "entities/topics/types";

export const StyledText = styled.p`
  margin: 0;
`;

const StyledContainer = styled.div`
  font-size: 0.5rem;
  color: ${({ theme }) => theme.colors.fillPrimary};

  // The breakpoint used by the transformation map for "bigger screens"
  @media (min-height: 900px) {
    font-size: 0.8vmin;
  }
`;

const StyledLink = styled(Link)`
  color: ${({ theme }) => theme.colors.blue};
  cursor: pointer;
`;

export const COPYRIGHT_DEFAULT_TEXT = `© ${new Date().getFullYear()} World Economic Forum.`;

type MapCopyrightTextProps = { doesMapContainCustomKeyIssues?: boolean };

export const MapCopyrightText = ({ doesMapContainCustomKeyIssues }: MapCopyrightTextProps) => {
  const translate = useTranslate();

  if (doesMapContainCustomKeyIssues) {
    const customText = interpolate(translate("app.transformation_map.copyright.other_portions_copyright"), {
      year: () => new Date().getFullYear(),
    });
    return <StyledText>{customText}</StyledText>;
  }
  return <StyledText>{COPYRIGHT_DEFAULT_TEXT}</StyledText>;
};

// personal/professional
// creator
//  -> custom map/forum map

type MapLicenseTextProps = { creator?: string; tmLicense?: string };

export const MapLicenseText = ({ tmLicense, creator }: MapLicenseTextProps) => {
  const translate = useTranslate();
  const tocLink = (tocText: string) => <StyledLink to="/terms-and-conditions">{tocText}</StyledLink>;

  return (
    <StyledText data-testid="map-license-text">
      {interpolate(translate(getTerm(creator, tmLicense)), {
        ...(creator === undefined ? {} : { creator: () => creator }),
        tocLink,
      })}
    </StyledText>
  );
};

const getTerm = (creator?: string, tmLicense?: string) => {
  if (tmLicense === "professional")
    return creator
      ? "app.transformation_map.copyright.custom_map_professional_license"
      : "app.transformation_map.copyright.professional_license";
  return creator
    ? "app.transformation_map.copyright.custom_map_personal_license"
    : "app.transformation_map.copyright.personal_license";
};

type CustomKeyIssueDisclaimerTextProps = { creator?: string; topicNames: string[] };

export const CustomKeyIssueDisclaimerText = ({ creator, topicNames }: CustomKeyIssueDisclaimerTextProps) => {
  const translate = useTranslate();

  if (!topicNames.length || !creator) {
    return null;
  }

  const translationsString = translate("app.transformation_map.copyright.custom_key_issues_disclamer");
  const disclaimerText = interpolate(translationsString, {
    creator: () => creator,
  });

  return (
    <>
      <StyledText>{disclaimerText}</StyledText>
      <StyledText>{topicNames.join(" | ")}.</StyledText>
    </>
  );
};

type TMCopyrightTextProps = { className?: string; creator?: string; topic?: Topic };

/*
topic without creator (forum maps), unauthenticated/public/DM users:
  Licensed for personal/academic use only, not for reproduction.
  © 2022 World Economic Forum

topic without creator (forum maps), pro user:
  Licensed for professional use
  © 2022 World Economic Forum

custom topic without custom key issues, unauthenticated/public/DM users:
  Created by ${topic-creator}, licensed for personal/academic use only, not for reproduction.
  © 2022 World Economic Forum

custom topic without custom key issues, pro user:
  Created by ${topic-creator}, Licenced for professional use
  © 2022 World Economic Forum

custom topic with custom key issues, unauthenticated/public/DM users:
  Created by ${topic-creator}, licensed for personal/academic use only, not for reproduction.
  The following topics are defined by ${topic-creator} and do not necessarily reflect the view of the World Economic Forum:
  ${custom-key-issue-names}
  Other portions © 2022 World Economic Forum

custom topic without custom key issues, pro user:
  Created by ${topic-creator}, Licenced for professional use
  The following topics are defined by ${topic-creator} and do not necessarily reflect the view of the World Economic Forum:
  ${custom-key-issue-names}
  Other portions © 2022 World Economic Forum
 */

export const TMCopyrightText = ({ topic, className, creator }: TMCopyrightTextProps) => {
  const tmLicense = useSelector(getTMLicense);

  if (!topic) {
    return null;
  }

  const customKeyIssueNames =
    topic.dimensions?.filter((keyIssue) => keyIssue.is_custom_dimension).map((keyIssue) => keyIssue.name) ?? [];

  return (
    <StyledContainer className={className}>
      <MapLicenseText tmLicense={tmLicense} creator={creator} />
      <CustomKeyIssueDisclaimerText topicNames={customKeyIssueNames} creator={creator} />
      <MapCopyrightText doesMapContainCustomKeyIssues={customKeyIssueNames.length > 0} />
    </StyledContainer>
  );
};
